import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { gridClasses } from "@mui/x-data-grid";
import { GridToolbarContainer } from "@mui/x-data-grid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import VideosFilterSelector from "components/VideosFilterSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import { NAVBAR_HEIGHT } from "constants/layout";
import { OVERLAY_NAMES } from "constants/Overlays";
import CommentActionsMenu from "components/CommentActionsMenu";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import {
  commentCategoryLabel,
  formatTimestamp,
} from "helpers/publicVideoComments";
import {
  deleteVideo,
  deleteVideoComment,
  getVideoComments,
  replyToVideoComment,
  restoreVideoComment,
  getVideoLibrary,
  setVideoCommentResolved,
  setVideoPublished,
} from "services/videoLibraryApi";

const PUBLIC_VIDEO_ORIGIN = "https://videos.communicationessentialsllc.com";

const isId = (value) => typeof value === "string" || typeof value === "number";

const isVideoRecord = (video) =>
  Boolean(
    video &&
      typeof video === "object" &&
      !Array.isArray(video) &&
      isId(video.id) &&
      typeof video.title === "string" &&
      (typeof video.description === "string" || video.description === null) &&
      typeof video.synthesiaEmbedUrl === "string" &&
      typeof video.publicId === "string" &&
      ["draft", "published", "unpublished"].includes(video.status) &&
      ["internal", "public"].includes(video.visibility),
  );

const isCommentRecord = (comment) =>
  Boolean(
    comment &&
      typeof comment === "object" &&
      !Array.isArray(comment) &&
      isId(comment.id) &&
      typeof comment.name === "string" &&
      typeof comment.body === "string" &&
      (comment.video === null ||
        (comment.video &&
          typeof comment.video === "object" &&
          typeof comment.video.title === "string")),
  );

// The sidebar badge listens for this so it updates without a page change.
const notifyVideoCommentsChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("videoCommentsChanged"));
  }
};

const STATUS_CHIP = {
  published: { label: "Published", color: "success" },
  draft: { label: "Draft", color: "default" },
  unpublished: { label: "Unpublished", color: "warning" },
};

// In dev the videos host is not live, so everything is served from the portal's
// own /videos/:publicId route (which falls back to the public API when signed out).
const IS_DEV = process.env.NODE_ENV !== "production";

const videoUrlFor = (video) => {
  if (video.status !== "published" || !video.publicId) return null;
  const encodedId = encodeURIComponent(video.publicId);

  if (IS_DEV) {
    return `${window.location.origin}/videos/${encodedId}`;
  }

  return `${PUBLIC_VIDEO_ORIGIN}/v/${encodedId}`;
};

const shortLinkLabel = (url) => {
  const path = url.replace(/^https?:\/\/[^/]+/, "");
  return path.length > 28 ? `${path.slice(0, 26)}…` : path;
};

const GRID_OPTION_COLUMNS = [
  { field: "title", label: "Title" },
  { field: "description", label: "Description" },
  { field: "publicId", label: "Link" },
  { field: "status", label: "Status" },
  { field: "visibility", label: "Visibility" },
];

/**
 * Per-row ⋮ menu. Owns its open/close state (hooks cannot live inside a
 * renderCell callback), mirroring the overflow menu used on Linguists Payments.
 */
const RowActionsMenu = ({ video, isBusy, onEdit, onTogglePublish, onDelete }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const isPublished = video.status === "published";

  const openMenu = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const runAction = (action) => (event) => {
    event.stopPropagation();
    closeMenu();
    action();
  };

  return (
    <>
      <Tooltip title="More actions">
        <span>
          <IconButton
            size="small"
            aria-label={`More actions for ${video.title}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={openMenu}
            disabled={isBusy}
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.divider}`,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <MoreVertRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { minWidth: 180, borderRadius: "10px" } },
          list: { dense: true },
        }}
      >
        <MenuItem onClick={runAction(onEdit)}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem onClick={runAction(onTogglePublish)}>
          <ListItemIcon>
            {isPublished ? (
              <VisibilityOffRoundedIcon fontSize="small" />
            ) : (
              <VisibilityRoundedIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{isPublished ? "Unpublish" : "Publish"}</ListItemText>
        </MenuItem>

        <Divider />

        <Tooltip
          title={isPublished ? "Unpublish the video before deleting it" : ""}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={runAction(onDelete)}
              disabled={isPublished}
              sx={{ color: theme.palette.error.main }}
            >
              <ListItemIcon>
                <DeleteRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.palette.error.main }}
                />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};

const VideoLibrary = () => {
  usePageTitle("Video Library");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { setOverlayName, setOverlayData, showSnackbar } = useStateContext();

  const [videos, setVideos] = useState([]);
  const [comments, setComments] = useState([]);
  const [openCommentCount, setOpenCommentCount] = useState(0);
  // Comments publish immediately, so the admin list is a work list: open, done,
  // or deleted.
  const [commentFilter, setCommentFilter] = useState("open");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [videosLoading, setVideosLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [videosError, setVideosError] = useState("");
  const [commentsError, setCommentsError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionKey, setActionKey] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [commentPendingDelete, setCommentPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedVisibilities, setSelectedVisibilities] = useState([]);

  const [density, setDensity] = useState("standard");
  const [columnsVisible, setColumnsVisible] = useState({});
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [gridOptionsMode, setGridOptionsMode] = useState(null);
  const [gridOptionsOpen, setGridOptionsOpen] = useState(false);
  const [gridOptionsAnchorEl, setGridOptionsAnchorEl] = useState(null);

  const loadVideos = useCallback(async () => {
    setVideosLoading(true);
    setVideosError("");
    try {
      const response = await getVideoLibrary();
      if (
        !Array.isArray(response.data?.videos) ||
        response.data.videos.some((video) => !isVideoRecord(video))
      ) {
        throw new Error("Unexpected video library response.");
      }
      setVideos(response.data.videos);
    } catch {
      setVideosError("The video list could not be loaded.");
    } finally {
      setVideosLoading(false);
    }
  }, []);

  const loadComments = useCallback(async (filter = "open") => {
    setCommentsLoading(true);
    setCommentsError("");
    try {
      const response =
        filter === "deleted"
          ? await getVideoComments({ deletedOnly: true })
          : await getVideoComments({ resolved: filter === "done" });
      const list = response.data?.comments;
      if (!Array.isArray(list) || list.some((comment) => !isCommentRecord(comment))) {
        throw new Error("Unexpected comments response.");
      }
      setComments(list);
    } catch {
      setCommentsError("Comments could not be loaded.");
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const loadOpenCommentCount = useCallback(async () => {
    try {
      const response = await getVideoComments({ resolved: false });
      const list = response.data?.comments;
      if (Array.isArray(list)) {
        setOpenCommentCount(list.length);
      }
    } catch {
      // The badge is informational; a failure must not block the page.
    }
  }, []);

  useEffect(() => {
    loadVideos();
    loadOpenCommentCount();
  }, [loadOpenCommentCount, loadVideos]);

  useEffect(() => {
    if (!commentsOpen) return;
    loadComments(commentFilter);
  }, [commentFilter, commentsOpen, loadComments]);

  const openCreate = () => {
    setOverlayData({
      Action: OVERLAY_NAMES.VIDEO_ACTION_NEW,
      onVideosSaved: loadVideos,
    });
    setOverlayName(OVERLAY_NAMES.MANAGEMENT_VIDEO_NEW_EDIT);
  };

  const openEdit = (video) => {
    setOverlayData({
      Action: OVERLAY_NAMES.VIDEO_ACTION_EDIT,
      video,
      onVideosSaved: loadVideos,
    });
    setOverlayName(OVERLAY_NAMES.MANAGEMENT_VIDEO_NEW_EDIT);
  };

  const handlePublishChange = async (video) => {
    const shouldPublish = video.status !== "published";
    const key = `video-${video.id}`;
    setActionKey(key);
    setActionError("");
    try {
      await setVideoPublished(video.id, shouldPublish);
      await loadVideos();
    } catch {
      setActionError("The video status could not be changed.");
    } finally {
      setActionKey("");
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard
      .writeText(url)
      .then(() => showSnackbar("Link copied to clipboard.", theme, "success"))
      .catch(() => showSnackbar("Failed to copy the link.", theme, "error"));
  };

  const handleDeleteVideo = async () => {
    if (!confirmDelete || deleting) return;
    const key = `video-${confirmDelete.id}`;
    setActionKey(key);
    setActionError("");
    setDeleting(true);
    try {
      await deleteVideo(confirmDelete.id);
      setConfirmDelete(null);
      await loadVideos();
    } catch (error) {
      setActionError(
        error?.response?.data?.error ||
          "The video could not be deleted. Please try again.",
      );
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
      setActionKey("");
    }
  };

  // "Done" is the admin's resolution marker now that nothing needs approving.
  const handleResolve = async (comment, resolved) => {
    const key = `comment-${comment.id}`;
    setActionKey(key);
    setCommentsError("");
    try {
      await setVideoCommentResolved(comment.id, resolved);
      // Drop it from a filtered list it no longer belongs to.
      setComments((current) => current.filter((entry) => entry.id !== comment.id));
      loadOpenCommentCount();
      notifyVideoCommentsChanged();
    } catch {
      setCommentsError("The comment could not be updated.");
    } finally {
      setActionKey("");
    }
  };

  const handleReply = async (comment) => {
    const draft = (replyDrafts[comment.id] || "").trim();
    if (!draft) return;
    const key = `comment-${comment.id}`;
    setActionKey(key);
    setCommentsError("");
    try {
      await replyToVideoComment(comment.id, draft);
      setReplyDrafts((current) => ({ ...current, [comment.id]: "" }));
      showSnackbar("Reply posted.", theme, "success");
      notifyVideoCommentsChanged();
    } catch (error) {
      setCommentsError(
        error?.response?.data?.error || "The reply could not be posted.",
      );
    } finally {
      setActionKey("");
    }
  };

  const handleRestore = async (comment) => {
    const key = `comment-${comment.id}`;
    setActionKey(key);
    setCommentsError("");
    try {
      await restoreVideoComment(comment.id);
      setComments((current) => current.filter((entry) => entry.id !== comment.id));
      loadOpenCommentCount();
      notifyVideoCommentsChanged();
    } catch {
      setCommentsError("The comment could not be restored.");
    } finally {
      setActionKey("");
    }
  };

  const confirmAdminDeleteComment = async () => {
    const comment = commentPendingDelete;
    if (!comment) return;
    setCommentPendingDelete(null);
    const key = `comment-${comment.id}`;
    setActionKey(key);
    setCommentsError("");
    try {
      await deleteVideoComment(comment.id);
      setComments((current) => current.filter((entry) => entry.id !== comment.id));
      loadOpenCommentCount();
      notifyVideoCommentsChanged();
    } catch {
      setCommentsError("The comment could not be deleted.");
    } finally {
      setActionKey("");
    }
  };

  // Status / visibility filtering lives here. Search is the grid's own quick
  // filter (CEQuickFilter in the toolbar), exactly like Linguists Payments.
  const columnFilteredData = useMemo(
    () =>
      videos.filter((video) => {
        if (
          selectedStatuses.length > 0 &&
          !selectedStatuses.includes(video.status)
        ) {
          return false;
        }
        if (
          selectedVisibilities.length > 0 &&
          !selectedVisibilities.includes(video.visibility)
        ) {
          return false;
        }
        return true;
      }),
    [videos, selectedStatuses, selectedVisibilities],
  );

  const hasAnyActiveFilters =
    selectedStatuses.length > 0 || selectedVisibilities.length > 0;

  const handleClearFilters = () => {
    setSelectedStatuses([]);
    setSelectedVisibilities([]);
  };

  const columns = useMemo(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 1.1,
        minWidth: 220,
        renderCell: (params) => (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ overflowWrap: "anywhere" }}
          >
            {params.row.title}
          </Typography>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            title={params.row.description || ""}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.description || "—"}
          </Typography>
        ),
      },
      {
        field: "publicId",
        headerName: "Link",
        flex: 1.6,
        minWidth: 260,
        renderCell: (params) => {
          const url = videoUrlFor(params.row);
          if (!url) {
            return (
              <Typography variant="body2" color="text.secondary">
                Publish to get a link
              </Typography>
            );
          }
          return (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ minWidth: 0, width: "100%" }}
            >
              <Typography
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                title={url}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  color: theme.palette.primary.main,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {shortLinkLabel(url)}
              </Typography>
              <Tooltip title="Open link">
                <IconButton
                  size="small"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${params.row.title}`}
                  onClick={(event) => event.stopPropagation()}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy link">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyLink(url);
                  }}
                  aria-label={`Copy link for ${params.row.title}`}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => {
          const chip = STATUS_CHIP[params.row.status] || STATUS_CHIP.draft;
          return <Chip size="small" label={chip.label} color={chip.color} />;
        },
      },
      {
        field: "visibility",
        headerName: "Visibility",
        width: 140,
        renderCell: (params) => (
          <Chip
            size="small"
            variant="outlined"
            label={
              params.row.visibility === "public" ? "Public" : "Internal Only"
            }
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 64,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <RowActionsMenu
            video={params.row}
            isBusy={actionKey === `video-${params.row.id}`}
            onEdit={() => openEdit(params.row)}
            onTogglePublish={() => handlePublishChange(params.row)}
            onDelete={() => setConfirmDelete(params.row)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actionKey, theme.palette.primary.main],
  );

  const handleOptionsClick = (event) => {
    setOptionsAnchorEl(event.currentTarget);
    setOptionsOpen(true);
  };

  const handleOptionsClose = () => {
    setOptionsOpen(false);
  };

  // Mirrors Linguists Payments: the screen toolbar owns the search bar
  // (CEQuickFilter on the grid, QuickFilterField in card mode) and the Grid
  // Options menu. Memoised so its identity is stable — an inline component
  // would remount and drop focus while typing.
  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ cardMode, quickFilterValue, onQuickFilterChange }) => {
      const isMobileToolbar = isMobile;

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          <Box sx={{ flexGrow: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={handleClearFilters}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor:
                      theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobileToolbar} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  setGridOptionsMode("density");
                  setGridOptionsOpen(true);
                  setGridOptionsAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setGridOptionsMode("columns");
                  setGridOptionsOpen(true);
                  setGridOptionsAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    hasAnyActiveFilters,
    isMobile,
    optionsAnchorEl,
    optionsOpen,
    theme,
  ]);

  const GridOptionsOverlay = () => {
    if (gridOptionsMode === "density") {
      return (
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setGridOptionsOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(event) => setDensity(event.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      );
    }

    if (gridOptionsMode === "columns") {
      return (
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Columns
            </Typography>
            <IconButton
              size="small"
              onClick={() => setGridOptionsOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          {GRID_OPTION_COLUMNS.map((column) => (
            <FormControlLabel
              key={column.field}
              sx={{ display: "flex", ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={columnsVisible[column.field] !== false}
                  onChange={(event) =>
                    setColumnsVisible((current) => ({
                      ...current,
                      [column.field]: event.target.checked,
                    }))
                  }
                />
              }
              label={column.label}
            />
          ))}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: isMobile ? "auto" : FILTER_SELECTOR_WIDTH,
                }}
              >
                <VideosFilterSelector
                  selectedStatuses={selectedStatuses}
                  selectedVisibilities={selectedVisibilities}
                  onSave={({ statuses, visibilities }) => {
                    setSelectedStatuses(statuses);
                    setSelectedVisibilities(visibilities);
                  }}
                />
              </Box>

              <Box display="flex" gap={isMobile ? 1 : 2} alignItems="center">
                <Tooltip title="Review pending comments">
                  <Button
                    onClick={() => setCommentsOpen(true)}
                    startIcon={<RateReviewRoundedIcon />}
                    sx={{
                      minWidth: isMobile ? 40 : 200,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: isMobile ? "8px" : "8px 16px",
                      backgroundColor: theme.palette.background.default,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": { marginRight: 0 },
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    {!isMobile && (
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        Comments
                        {openCommentCount > 0 ? ` (${openCommentCount})` : ""}
                      </Box>
                    )}
                  </Button>
                </Tooltip>

                <Button
                  onClick={openCreate}
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    minWidth: isMobile ? 40 : 200,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    padding: isMobile ? "8px" : "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": { marginRight: 0 },
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {!isMobile && (
                    <Box sx={{ flex: 1, textAlign: "center" }}>Add Video</Box>
                  )}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {(videosError || actionError) && (
          <Alert severity="error" role="alert" sx={{ mt: 1 }}>
            {videosError || actionError}
          </Alert>
        )}
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
            display: "flex",
            alignItems: "center",
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGridResponsive
          localeText={{
            noRowsLabel: "No videos yet. Use Add Video to get started.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          loading={videosLoading}
          rows={columnFilteredData}
          columns={columns}
          getRowId={(row) => row.id}
          density={density}
          columnVisibilityModel={columnsVisible}
          onColumnVisibilityModelChange={(model) => setColumnsVisible(model)}
          disableColumnMenu
          disableRowSelectionOnClick
          sx={{
            // Clicking a row navigates immediately; without this the clicked cell
            // paints a focus ring for a frame on the way out.
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
          }}
          pageSizeOptions={[20, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
            sorting: { sortModel: [{ field: "title", sort: "asc" }] },
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              hasAnyActiveFilters,
              onClear: handleClearFilters,
            },
          }}
          onRowClick={(params) =>
            navigate(`/video-library/${encodeURIComponent(params.row.id)}`)
          }
          mobile={{
            primaryField: "title",
            badgeField: "status",
            multilineFields: ["description"],
            pageSize: 20,
            selectable: false,
            contextMenuButton: false,
          }}
        />
      </Box>

      {gridOptionsOpen && gridOptionsMode && (
        <Popover
          open={gridOptionsOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            gridOptionsAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setGridOptionsOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}

      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => {
          if (!deleting) setConfirmDelete(null);
        }}
        aria-labelledby="video-delete-dialog-title"
      >
        <DialogTitle id="video-delete-dialog-title">Delete this video?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            <strong>{confirmDelete?.title}</strong> will be removed permanently.
            Its public link will stop working and its comments will be deleted
            with it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteVideo}
            loading={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(commentPendingDelete)}
        onClose={() => setCommentPendingDelete(null)}
        aria-labelledby="comment-delete-dialog-title"
      >
        <DialogTitle id="comment-delete-dialog-title">Delete this comment?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            It is hidden from the public page but kept, so you can restore it from
            the Deleted tab.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmAdminDeleteComment}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="video-comments-dialog-title"
      >
        <DialogTitle
          id="video-comments-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          Comments
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant={commentFilter === "open" ? "contained" : "text"}
            onClick={() => setCommentFilter("open")}
          >
            Open
          </Button>
          <Button
            size="small"
            variant={commentFilter === "done" ? "contained" : "text"}
            onClick={() => setCommentFilter("done")}
          >
            Done
          </Button>
          <Button
            size="small"
            variant={commentFilter === "deleted" ? "contained" : "text"}
            onClick={() => setCommentFilter("deleted")}
          >
            Deleted
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {commentsLoading && (
            <Box display="flex" alignItems="center" gap={1} py={2}>
              <CircularProgress size={22} />
              <Typography>Loading comments…</Typography>
            </Box>
          )}
          {commentsError && <Alert severity="error">{commentsError}</Alert>}
          {!commentsLoading && !commentsError && comments.length === 0 && (
            <Typography color="text.secondary">
              {commentFilter === "deleted"
                ? "No comments have been deleted."
                : commentFilter === "done"
                  ? "Nothing has been marked done yet."
                  : "No open comments."}
            </Typography>
          )}
          {comments.length > 0 && (
            <Stack spacing={1.5}>
              {comments.map((comment) => {
                const isBusy = actionKey === `comment-${comment.id}`;
                return (
                  <Box
                    key={comment.id}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: "12px",
                      p: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {comment.video?.title || "Video unavailable"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {comment.name}
                      </Typography>
                      {typeof comment.email === "string" && comment.email && (
                        <Typography
                          component="a"
                          href={`mailto:${comment.email}`}
                          variant="body2"
                          color="text.secondary"
                        >
                          {comment.email}
                        </Typography>
                      )}
                      <Chip
                        size="small"
                        label={commentCategoryLabel(comment.category)}
                      />
                      {Number.isFinite(comment.timestampSeconds) && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={formatTimestamp(comment.timestampSeconds)}
                        />
                      )}
                      {isId(comment.parentCommentId) && (
                        <Chip size="small" label="Reply" />
                      )}
                      {comment.isAdmin && (
                        <Chip size="small" color="primary" label="Team" />
                      )}
                      {comment.resolvedAt && (
                        <Chip size="small" color="success" label="Done" />
                      )}
                      {commentFilter === "deleted" && comment.deletedAt && (
                        <Chip
                          size="small"
                          label={
                            comment.deletedBy === "commenter"
                              ? "Deleted by author"
                              : "Deleted by admin"
                          }
                        />
                      )}
                      {commentFilter === "deleted" && !comment.deletedAt && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Shown for context"
                        />
                      )}
                      <CommentActionsMenu
                        comment={comment}
                        isBusy={isBusy}
                        onResolve={() =>
                          handleResolve(comment, !comment.resolvedAt)
                        }
                        onRestore={() => handleRestore(comment)}
                        onDelete={() => setCommentPendingDelete(comment)}
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {comment.body}
                    </Typography>

                    {commentFilter !== "deleted" && (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1 }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          maxRows={4}
                          placeholder="Reply as the team…"
                          value={replyDrafts[comment.id] || ""}
                          onChange={(event) =>
                            setReplyDrafts((current) => ({
                              ...current,
                              [comment.id]: event.target.value,
                            }))
                          }
                          inputProps={{
                            "aria-label": `Reply to ${comment.name}`,
                            maxLength: 1000,
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          disabled={isBusy || !(replyDrafts[comment.id] || "").trim()}
                          onClick={() => handleReply(comment)}
                        >
                          Reply
                        </Button>
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoLibrary;
